import { Link } from "react-router-dom";
import { Megaphone, Layout as LayoutIcon, Play, Sparkles, Mail, CheckCircle2 } from "lucide-react";
import useSEO from "../hooks/useSEO";

const FORMATS = [
  { name: "Bandeau leaderboard", size: "970 × 250 px", spot: "Accueil (haut) · Pages annuaire", desc: "Idéal pour les annonces institutionnelles et les campagnes de notoriété.", price: "Sur devis" },
  { name: "Rectangle médium", size: "300 × 250 px", spot: "Pages détail · Blog · Barre latérale", desc: "Format polyvalent pour promotions ciblées et micro-campagnes.", price: "Sur devis" },
  { name: "Bannière horizontale", size: "728 × 90 px", spot: "Milieu de page · Pied d'article", desc: "Format classique pour des messages courts et efficaces.", price: "Sur devis" },
  { name: "Article sponsorisé", size: "Long-form", spot: "Blog / Médiathèque", desc: "Un contenu éditorial dédié à votre marque, écrit avec notre équipe.", price: "Sur devis" },
  { name: "Emplacement vidéo", size: "Ratio 16:9", spot: "Accueil (à côté du bandeau)", desc: "Diffusez votre présentation vidéo auprès de notre communauté francophone.", price: "Sur devis" },
];

const REACH = [
  { label: "Communauté francophone Canada-Afrique", value: "En croissance" },
  { label: "Nouveaux arrivants et entrepreneurs immigrants", value: "Cible qualifiée" },
  { label: "Canaux DemkaBiz", value: "Web · Telegram · Réseaux sociaux" },
];

export default function EspacePublicite() {
  useSEO({
    title: "Espace publicité — Faites rayonner votre marque",
    description: "Réservez un espace publicitaire sur DemkaBiz : bannières, articles sponsorisés et emplacements vidéo pour toucher la communauté francophone du Canada.",
  });
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20" data-testid="ads-page">
      <div className="flex items-center gap-3 text-accent mb-3">
        <Megaphone className="w-6 h-6" />
        <p className="text-xs uppercase tracking-widest font-semibold">Espace publicité</p>
      </div>
      <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-foreground tracking-tight leading-[1.05]">
        Faites rayonner<br /><span className="text-accent italic">votre marque</span> ici.
      </h1>
      <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed">
        Touchez une communauté francophone hautement qualifiée : nouveaux arrivants, entrepreneurs, professionnels et organismes actifs au Canada. Formats variés, tarifs adaptés à chaque objectif.
      </p>

      <div className="grid md:grid-cols-3 gap-4 mt-10">
        {REACH.map((r, i) => (
          <div key={i} className="rounded-2xl bg-secondary/60 border border-primary/10 p-5 text-center" data-testid={`ads-reach-${i}`}>
            <div className="font-display font-bold text-xl text-primary">{r.value}</div>
            <div className="text-xs text-muted-foreground mt-1">{r.label}</div>
          </div>
        ))}
      </div>

      <section className="mt-14">
        <h2 className="font-display font-bold text-2xl sm:text-3xl">Formats disponibles</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {FORMATS.map((f, i) => (
            <div key={i} className="card-surface p-6 flex flex-col" data-testid={`ads-format-${i}`}>
              <div className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                {f.name.includes("vidéo") ? <Play className="w-5 h-5" /> : f.name.includes("Article") ? <Sparkles className="w-5 h-5" /> : <LayoutIcon className="w-5 h-5" />}
              </div>
              <h3 className="font-display font-semibold text-base">{f.name}</h3>
              <p className="text-xs uppercase tracking-wider text-accent font-semibold mt-1">{f.size}</p>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed flex-1">{f.desc}</p>
              <div className="mt-4 pt-4 border-t border-primary/5 text-xs text-muted-foreground">
                <span className="block"><strong className="text-foreground">Emplacement :</strong> {f.spot}</span>
                <span className="block mt-1"><strong className="text-foreground">Tarif :</strong> {f.price}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14 card-surface p-6 sm:p-10">
        <h2 className="font-display font-bold text-2xl sm:text-3xl">Nos engagements</h2>
        <ul className="grid sm:grid-cols-2 gap-3 mt-4 text-sm">
          {[
            "Sélection éditoriale des annonceurs (cohérence avec nos valeurs)",
            "Rapports de performance mensuels (impressions, clics)",
            "Formats et créations validés ensemble avant mise en ligne",
            "Support humain francophone dédié",
          ].map((e, i) => (
            <li key={i} className="flex items-start gap-2 text-foreground/85"><CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" /> {e}</li>
          ))}
        </ul>
      </section>

      <section className="mt-14 rounded-3xl bg-accent text-white p-8 sm:p-12 grid lg:grid-cols-[1fr_auto] items-center gap-6" data-testid="ads-cta-band">
        <div>
          <h2 className="font-display font-bold text-2xl sm:text-3xl leading-tight">Prêt à réserver votre espace ?</h2>
          <p className="text-white/90 mt-2 max-w-2xl">Écrivez-nous avec votre objectif, votre budget et le format qui vous intéresse. Nous vous répondons sous 48 heures ouvrables.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
          <a href="mailto:contact@demka.ca?subject=Espace%20publicit%C3%A9%20DemkaBiz" data-testid="ads-cta-email" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white text-accent hover:bg-white/90 transition font-medium">
            <Mail className="w-4 h-4" /> contact@demka.ca
          </a>
          <Link to="/contact?sujet=publicite" data-testid="ads-cta-contact" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 transition font-medium">
            Formulaire de contact
          </Link>
        </div>
      </section>
    </div>
  );
}
